/**
 * A chart's fixed axis bounds (`config.yMin` / `config.yMax`) in the org's units (#1190 review MEDIUM 2). The editor
 * saves the bounds with the unit they were typed in (`config.yUnit`); a chart draws them in the unit its data is shown
 * in, so an org that changes its units, or a board built in one and read in another, keeps its axis on its data. A
 * board saved before this holds its bounds in the data's stored unit, as every reading was then. Pure.
 */
import { convertBetween, normalise, UnitConversionError } from "@/lib/units/convert";
import type { DataSource } from "./types";

/** The data an axis follows: the unit it is shown in, and the unit it is stored in where they differ. */
export interface AxisDatum {
  unit: string;
  storedUnit?: string;
}

/**
 * The data a chart's axis follows: its bound tags whose live readings carry a unit, where they are all stored in one
 * unit (the first of them, in slot order). Undefined for a chart with no live readings (an imported dataset, the
 * fixtures), and for one whose tags are stored in different units, as no one unit is its axis's (#1190 review LOW):
 * its bounds are drawn as saved.
 */
export function axisDatumOf(
  ds: Pick<DataSource, "slots" | "keys"> | undefined,
  live: Record<string, AxisDatum> | undefined,
): AxisDatum | undefined {
  if (!ds || !live) return undefined;
  const keys = [...Object.values(ds.slots ?? {}).flat(), ...(ds.keys ?? [])];
  let first: AxisDatum | undefined;
  for (const key of keys) {
    const d = live[key.toLowerCase()];
    if (!d?.unit) continue;
    if (!first) {
      first = d.storedUnit ? { unit: d.unit, storedUnit: d.storedUnit } : { unit: d.unit };
    } else if (normalise(d.storedUnit ?? d.unit) !== normalise(first.storedUnit ?? first.unit)) {
      return undefined;
    }
  }
  return first;
}

const boundOf = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);

/** A number moved between two units; null where the two cannot be converted. */
function moved(v: number, from: string, to: string): number | null {
  try {
    return convertBetween(v, from, to);
  } catch (err) {
    if (err instanceof UnitConversionError) return null;
    throw err;
  }
}

/** The bounds moved from the unit they were saved in to `to`; the same object where nothing moves. */
function boundsIn<C extends Record<string, unknown>>(config: C, from: string, to: string): C {
  const yMin = boundOf(config.yMin);
  const yMax = boundOf(config.yMax);
  if ((yMin === undefined && yMax === undefined) || !from || !to || normalise(from) === normalise(to)) return config;
  const lo = yMin === undefined ? undefined : moved(yMin, from, to);
  const hi = yMax === undefined ? undefined : moved(yMax, from, to);
  if (lo === null || hi === null) return config;
  return { ...config, ...(lo === undefined ? {} : { yMin: lo }), ...(hi === undefined ? {} : { yMax: hi }) };
}

/** The unit a config's bounds were saved in: its own, else the data's stored unit (a board saved before units). */
const savedUnitOf = (config: Record<string, unknown>, storedUnit: string) =>
  typeof config.yUnit === "string" && config.yUnit ? config.yUnit : storedUnit;

/**
 * A widget's config with its bounds in the unit the chart draws its data in; the very same config where nothing moves
 * (a metric org's board, a board with no bounds, a chart with no live data, units that do not convert).
 */
export function configWithAxisShown<C extends Record<string, unknown>>(config: C, datum: AxisDatum | undefined): C;
export function configWithAxisShown<C extends Record<string, unknown>>(config: C | undefined, datum: AxisDatum | undefined): C | undefined;
export function configWithAxisShown<C extends Record<string, unknown>>(config: C | undefined, datum: AxisDatum | undefined): C | undefined {
  if (!config || !datum) return config;
  return boundsIn(config, savedUnitOf(config, datum.storedUnit ?? datum.unit), datum.unit);
}

/** The bounds as the editor shows them: in the unit the chart is shown in. */
export function axisBoundsShown(
  config: Record<string, unknown> | undefined,
  storedUnit: string,
  shownUnit: string,
): { yMin: number | undefined; yMax: number | undefined } {
  const c = config ? boundsIn(config, savedUnitOf(config, storedUnit), shownUnit) : {};
  return { yMin: boundOf(c.yMin), yMax: boundOf(c.yMax) };
}

/**
 * What the editor saves: the bounds as typed, with the unit they were typed in (the chart's shown unit). No unit where
 * the bounds are cleared, or where the chart's unit is not known (the bounds are then read as the stored unit).
 */
export function axisPatch(
  next: { yMin?: number; yMax?: number },
  shownUnit: string,
): { yMin: number | undefined; yMax: number | undefined; yUnit: string | undefined } {
  const any = next.yMin !== undefined || next.yMax !== undefined;
  return { yMin: next.yMin, yMax: next.yMax, yUnit: any && shownUnit ? shownUnit : undefined };
}
